import store from '../../../store';
import { informationActions } from '../../../store/informationSlice';
import { TRequireOnlyOne } from '../../../types';
import EventEmitter, { TEventKey } from '../../../utils/util/eventEmitter';

export type TTab = {
  asaccordion?: boolean;
  children: React.ReactNode;
  defaultCollapsed?: boolean;
  defaultOpen?: boolean;
  id: string | number;
  label: string;
  itemClassName?: string;
  marked?: boolean;
  open?: boolean;
  props?: Record<string, unknown>;
  tooltip: string;
};

export type TToggleEvent = {
  id: TTab['id'];
  open: boolean;
  history: TTab[];
  focus: boolean;
};

declare global {
  interface Window {
    TabsController: typeof TabsController;
  }
}

const TabsController = new (class extends EventEmitter<{
  open: TToggleEvent;
  update: TTab[];
  toggle: TToggleEvent;
}> {
  tabs: TTab[] = [];

  #maxFocusedHistory = 10;

  #focusedHistory: TTab['id'][] = [];

  allowMultiple = true;

  get history() {
    return this.#focusedHistory
      .map((historyId) => this.tabs.find((tab) => tab.id === historyId))
      .filter((tab): tab is TTab => !!tab);
  }

  get tabsArray() {
    return [...this.tabs];
  }

  clearAll() {
    this.tabs = [];
  }

  getTabById(id: TTab['id']) {
    return this.tabs.find((el) => el.id === id);
  }

  get open() {
    return this.tabs
      .map((current) => {
        return current.open ? current.id : -1;
      })
      .filter((current) => !(typeof current === 'number'));
  }

  add(
    tab: TTab,
    condition?: TRequireOnlyOne<{
      after: TTab['id'];
      before: TTab['id'];
    }>,
  ) {
    if (this.tabs.find((search) => search.id === tab.id)) {
      this.update(String(tab.id), tab);
      return;
    }

    let index: number = this.tabs.length;

    if (condition && condition.before) {
      const indexOfInsert = this.tabs.findIndex(
        (currentTab) => currentTab.id === condition.before,
      );
      if (indexOfInsert !== -1) index = indexOfInsert;
    }
    if (condition && condition.after) {
      const indexOfInsert = this.tabs.findIndex(
        (currentTab) => currentTab.id === condition.after,
      );
      if (indexOfInsert !== -1) index = indexOfInsert + 1;
    }

    index = Math.max(0, index);

    const currentTab = window.CURRENT_TAB?.split('~')[0].split(',') ?? [];
    this.tabs = [...this.tabs.splice(0, index), tab, ...this.tabs];
    this.tabs = this.tabs.map((current, i) => {
      return {
        ...current,
        open:
          (this.allowMultiple ||
            (!this.allowMultiple && this.open.length === 0)) &&
          (currentTab.includes(String(current.id)) ||
            currentTab.includes(String(i)) ||
            current.defaultOpen ||
            (currentTab.length === 0 &&
              !this.tabs.find((search) => search.defaultOpen) &&
              (current.open || i === 0))),
      };
    });

    this.#emitUpdate();
  }

  emit(
    eventName: TEventKey<{
      open: TToggleEvent;
      update: TTab[];
      toggle: TToggleEvent;
    }>,
    params?: TToggleEvent | TTab[] | undefined,
  ) {
    super.emit(eventName, params);
    store.dispatch(
      informationActions.update({
        id: 'tabsController',
        currentTabs: this.open,
      }),
    );
  }

  #emitUpdate() {
    store.dispatch(
      informationActions.update({
        id: 'tabs',
        list: this.tabs.map((tab) => ({ ...tab, children: null })),
      }),
    );
    this.emit('update', this.tabs);
  }

  openTab(id: TTab['id'], closeOthers = true) {
    window.dispatchEvent(
      new CustomEvent('sectionTabOpen', {
        detail: { id, justThis: !closeOthers },
      }),
    );

    if (closeOthers) {
      window.requestAnimationFrame(() => {
        window.scrollTo(0, 0);
      });
    }
    this.tabs = this.tabs.map((current) => ({
      ...current,
      open:
        current.id === id ||
        (!closeOthers && this.allowMultiple && current.open),
    }));
    this.#emitUpdate();

    this.emit('open', {
      id,
      open: true,
      history: this.history,
      focus: true,
    });
  }

  openTabs(index: number[]) {
    this.tabs = this.tabs.map((c, i) => ({ ...c, open: index.includes(i) }));
    this.#emitUpdate();
  }

  remove(id: TTab['id']) {
    this.tabs = this.tabs.filter((tab) => tab.id !== id);
    if (!this.tabs.find((current) => current.open))
      this.tabs[0] = { ...this.tabs[0], open: true };
    this.#emitUpdate();
  }

  toggle(id: TTab['id'], open: boolean, focus = true) {
    if (!this.allowMultiple) {
      this.openTab(id, true);
      return;
    }

    const currentOpened = this.tabs.filter((tab) => tab.open === true).length;

    if (open === false && currentOpened === 1) return;

    this.update(id, { open });

    if (open) {
      if (
        !(
          this.#focusedHistory[this.#focusedHistory.length - 1] === id &&
          this.#focusedHistory[this.#focusedHistory.length - 2] === id
        )
      ) {
        this.#focusedHistory.push(id);
        this.#focusedHistory.splice(
          0,
          Math.max(0, this.#focusedHistory.length - this.#maxFocusedHistory),
        );
      }
    }

    this.emit('toggle', {
      id,
      open,
      history: this.history,
      focus,
    });
  }

  update(id: TTab['id'], newProps: Omit<Partial<TTab>, 'id'>) {
    this.tabs = this.tabs.map((tab) => {
      if (tab.id === id) {
        return { ...tab, ...newProps };
      }
      return { ...tab };
    });

    this.#emitUpdate();
  }

  setOnApiaUtil() {
    Object.assign(window.ApiaUtil, {
      getSectionsLength: () => {
        console.warn(
          'This method will be replaced by ApiaUtil.execution.getSectionsLength in future versions',
        );
        return this.tabsArray.length;
      },
      setOpenSections: (sections: number[]) => {
        console.warn(
          'This method will be replaced by ApiaUtil.execution.setOpenSections in future versions',
        );
        this.tabsArray.forEach((c, i) => {
          this.update(c.id, { open: sections.includes(i) });
        });
      },
    });
  }
})();

window.TabsController = TabsController;

export default TabsController;
